import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useCallback, useEffect, useRef } from 'react'
import { isOfficeInitialized } from '../helpers/outlookHelper'

export type OfficeEventHandlerSignature = {
    id: string
    eventType: Office.EventType
    handle: (event: any) => void
    complete?: (result: Office.AsyncResult<any>) => void
}

type OfficeEventHandlerType = {
    addHandler: (handler: OfficeEventHandlerSignature) => void
    removeHandler: (id: string) => void
}

export type OfficeDragAndDropEventArgs = {
    dragAndDropEventData: {
        type: string
        dataTransfer: {
            files: {
                fileContent: Blob
                name: string
                type: string
            }[]
        }
    }
}

// Noop defaults so a component rendered outside the provider silently gets no Office events instead of
// crashing the whole tree on `addHandler is not a function` (HAM-4310).
const OfficeEventHandlerContext = createContext<OfficeEventHandlerType>({
    addHandler: () => console.warn('addHandler called outside of an OfficeEventHandlerProvider'),
    removeHandler: () => console.warn('removeHandler called outside of an OfficeEventHandlerProvider'),
})

export function OfficeEventHandlerProvider(props: PropsWithChildren): ReactElement {
    const callbackHandlers = useRef<OfficeEventHandlerSignature[]>([])
    const setCallbackHandlers = (items: OfficeEventHandlerSignature[]) => {
        callbackHandlers.current = items
    }

    /**
     * Only the handler that registered last gets the event (HAM-4313). An Office event carries no target element, so
     * without this every mounted Dropzone would receive the same drop: dropping an e-mail on the task dialog also
     * landed on the document overview behind it, which then asked for an import folder. Registration order stands in
     * for stacking order - a dialog mounts after the screen underneath it.
     */
    const handleHandlersByEventType = useCallback(
        (eventType: Office.EventType, event: any): void => {
            const handlers = callbackHandlers.current.filter(handler => handler.eventType === eventType)
            handlers[handlers.length - 1]?.handle(event)
        },
        [callbackHandlers],
    )
    // Registration feedback from Office, not an event: every handler must be able to report its own failure.
    const completeHandlersByEventType = useCallback(
        (eventType: Office.EventType, result: Office.AsyncResult<any>): void => {
            callbackHandlers.current.filter(handler => handler.eventType === eventType)?.forEach(callback => callback.complete?.(result))
        },
        [callbackHandlers],
    )

    useEffect(() => {
        if (isOfficeInitialized() && Office.context.platform === Office.PlatformType.OfficeOnline) {
            Office.context.mailbox.addHandlerAsync(
                Office.EventType.DragAndDropEvent,
                (event: OfficeDragAndDropEventArgs) => handleHandlersByEventType(Office.EventType.DragAndDropEvent, event),
                (result: Office.AsyncResult<any>) => completeHandlersByEventType(Office.EventType.DragAndDropEvent, result),
            )
        }
    }, [])

    return (
        <OfficeEventHandlerContext.Provider
            value={{
                addHandler: (handler: OfficeEventHandlerSignature) => {
                    setCallbackHandlers([...callbackHandlers.current, handler])
                },
                removeHandler: (id: string) => {
                    setCallbackHandlers([...callbackHandlers.current.filter(handler => handler.id !== id)])
                },
            }}
        >
            {props.children}
        </OfficeEventHandlerContext.Provider>
    )
}

// noinspection JSUnusedGlobalSymbols
export default OfficeEventHandlerContext
